import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { useSettingsStore } from '@/state/useSettingsStore'
import { describeMilestone, formatCurrentGoal } from '@/milestones/labels'
import { RankMedallion } from './RankMedallion'
import { getRingTone } from './RankRing'
import { useEffectiveTiers } from '@/milestones/useEffectiveTiers'
import type { Category } from '@/milestones/types'

// Spolehlivost a Servis se počítají z incidentů, které zatím nesbíráme —
// dokud nebude napojený support systém, nemají na dashboardu co ukazovat.
const CATEGORY_ORDER: Category[] = ['growth', 'revenue']

export function HeaderRankBar() {
  const { t } = useTranslation()
  const locale = useSettingsStore((s) => s.locale)
  const { tiers, goalByCategory, tierProgressByCategory, defs } = useEffectiveTiers()

  return (
    <div className="flex items-stretch gap-3 px-8 py-3">
      {CATEGORY_ORDER.map((cat) => {
        const tier = tiers[cat]
        const cg = goalByCategory[cat]
        const tp = tierProgressByCategory[cat]
        const tone = getRingTone(tier)
        const pct = Math.round(tp.progress * 100)
        const chipText = formatCurrentGoal(cg, cat, locale)
        const nextDef = defs.find(
          (d) => d.category === cat && d.direction === 'gte' && d.threshold === cg.goal,
        )
        const goalLabel = nextDef ? describeMilestone(nextDef, locale).title : ''

        return (
          <motion.article
            key={cat}
            layout
            className="relative flex min-w-0 flex-1 items-center gap-3 overflow-hidden rounded-2xl border border-border bg-surface-1/70 px-3 py-2"
          >
            <span
              aria-hidden="true"
              className="pointer-events-none absolute -inset-4 -z-0 rounded-full opacity-25 blur-3xl"
              style={{
                background: `radial-gradient(closest-side, ${tone.outer}99 0%, transparent 70%)`,
              }}
            />

            <div className="shrink-0">
              <RankMedallion category={cat} tier={tier} size={64} />
            </div>

            <div className="min-w-0 flex-1 space-y-1">
              <div className="flex items-center justify-between gap-2">
                <span className="truncate text-sm font-semibold text-ink">
                  {t(`milestones.category.${cat}`)}
                </span>
                <span
                  className="shrink-0 whitespace-nowrap text-[10px] font-extrabold uppercase tracking-widest"
                  style={{
                    background: `linear-gradient(135deg, ${tone.highlight}, ${tone.outer}, ${tone.shadow})`,
                    WebkitBackgroundClip: 'text',
                    backgroundClip: 'text',
                    color: 'transparent',
                  }}
                >
                  {chipText}
                </span>
              </div>
              <div className="truncate text-[10px] text-ink-subtle">
                {t(`milestones.categorySub.${cat}`)}
              </div>
              <div className="relative h-1.5 overflow-hidden rounded-full bg-surface-3/70">
                <motion.div
                  className="h-full rounded-full"
                  style={{
                    background: `linear-gradient(90deg, ${tone.outer}, ${tone.highlight})`,
                  }}
                  initial={{ width: '0%' }}
                  animate={{ width: `${pct}%` }}
                  transition={{ duration: 0.9, ease: 'easeOut' }}
                />
                {pct >= 90 && (
                  <span className="pointer-events-none absolute inset-0 animate-pulse bg-gradient-to-r from-transparent via-white/40 to-transparent" />
                )}
              </div>
              <div className="flex items-center justify-between text-[10px] text-ink-muted">
                <span className="truncate">{goalLabel}</span>
                <span className="ml-2 shrink-0 font-mono text-ink-subtle">T{tier}</span>
              </div>
            </div>
          </motion.article>
        )
      })}
    </div>
  )
}
